/**
 * Pre-Completion Checklist Configuration
 * 
 * Defines all sections and items for the pre-completion workflow.
 * Available only after exchange has been completed on a matter.
 */

const PRE_COMPLETION_CONFIG = {
  id: "pre_completion",
  title: "Pre-Completion Approval Checklist",
  subtitle: "Residential Conveyancing",
  pdfFooter: "This checklist is aligned with the Law Society Conveyancing Protocol (2019) and the Law Society Conveyancing Quality Scheme Core Practice Management Standards (Risk Management, File & Case Management, Client Care).",
  sections: [
    {
      id: "title_registration",
      title: "Title & Registration",
      items: [
        { id: "1.1", label: "Final title check — no new entries since exchange", type: "checkbox", applies: ["purchase", "linked"] },
        { id: "1.2", label: "OS1/OS2 priority search obtained (within priority period)", type: "checkbox_date", applies: ["purchase", "linked"] },
        { id: "1.3", label: "Bankruptcy search against purchaser(s) obtained", type: "checkbox_date", applies: ["purchase", "linked"] },
        { id: "1.4", label: "Company search (if company purchaser)", type: "checkbox", applies: ["purchase", "linked"], condition: "is_company" },
      ],
    },
    {
      id: "mortgage_finance",
      title: "Mortgage & Finance",
      items: [
        { id: "2.1", label: "Mortgage advance requested from lender", type: "checkbox_date", applies: ["purchase", "linked"], condition: "is_mortgaged", deadline_type: "mortgage_advance" },
        { id: "2.2", label: "Mortgage funds received into client account", type: "checkbox_amount", applies: ["purchase", "linked"], condition: "is_mortgaged" },
        { id: "2.3", label: "Redemption figure for seller's mortgage confirmed", type: "checkbox_amount", applies: ["sale", "linked"], condition: "is_mortgaged" },
        { id: "2.4", label: "DS1/electronic discharge confirmed with seller's lender", type: "checkbox", applies: ["purchase", "linked"] },
        { id: "2.5", label: "Completion funds calculation finalized", type: "checkbox", applies: ["sale", "purchase", "linked"] },
      ],
    },
    {
      id: "completion_statement",
      title: "Completion Statement",
      items: [
        { id: "3.1", label: "Final completion statement prepared", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "3.2", label: "Completion statement approved by client", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "3.3", label: "Apportionments calculated (ground rent, service charge, etc.)", type: "checkbox", applies: ["sale", "purchase", "linked"], condition: "is_leasehold" },
      ],
    },
    {
      id: "transfer_documents",
      title: "Transfer Documents",
      items: [
        { id: "4.1", label: "TR1 (transfer) signed by seller", type: "checkbox", applies: ["sale", "linked"] },
        { id: "4.2", label: "TR1 witnessed and dated", type: "checkbox", applies: ["sale", "linked"] },
        { id: "4.3", label: "Any additional documents signed (TP1, deed of covenant, etc.)", type: "checkbox_notes", applies: ["sale", "purchase", "linked"] },
        { id: "4.4", label: "SDLT/LTT return prepared", type: "checkbox", applies: ["purchase", "linked"] },
        { id: "4.5", label: "SDLT/LTT amount calculated and confirmed with client", type: "checkbox_amount", applies: ["purchase", "linked"] },
      ],
    },
    {
      id: "leasehold_specific",
      title: "Leasehold Specific",
      condition: "is_leasehold",
      items: [
        { id: "5.1", label: "Landlord's notice of transfer prepared", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "5.2", label: "Deed of covenant prepared (if required)", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "5.3", label: "Management company share transfer form prepared", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "5.4", label: "License to assign obtained (if required)", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "5.5", label: "Service charge/ground rent apportionments agreed", type: "checkbox", applies: ["sale", "purchase", "linked"] },
      ],
    },
    {
      id: "funds_banking",
      title: "Funds & Banking",
      items: [
        { id: "6.1", label: "Client's contribution received into client account", type: "checkbox_amount", applies: ["purchase", "linked"] },
        { id: "6.2", label: "Total completion monies in client account", type: "checkbox_amount", applies: ["purchase", "linked"] },
        { id: "6.3", label: "Telegraphic transfer form prepared for completion day", type: "checkbox", applies: ["purchase", "linked"] },
        { id: "6.4", label: "Agent's fees confirmed (if applicable)", type: "checkbox_amount", applies: ["sale", "linked"] },
      ],
    },
    {
      id: "undertakings_compliance",
      title: "Undertakings & Compliance",
      items: [
        { id: "7.1", label: "List of undertakings reviewed", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "7.2", label: "Undertaking to discharge seller's mortgage (Form of undertaking)", type: "checkbox", applies: ["purchase", "linked"] },
        { id: "7.3", label: "AML checks still within validity period", type: "checkbox", applies: ["sale", "purchase", "linked"], autoCheck: "thirdfort" },
        { id: "7.4", label: "Professional indemnity cover confirmed adequate", type: "checkbox", applies: ["sale", "purchase", "linked"] },
      ],
    },
    {
      id: "client_communication",
      title: "Client Communication",
      items: [
        { id: "8.1", label: "Completion date and time confirmed with all parties", type: "checkbox_date", applies: ["sale", "purchase", "linked"], deadline_type: "completion_date", tooltip: "If this date differs from the Pre-Exchange completion date (item 5.1), a warning will appear and you must record a reason for the discrepancy. The change is logged and notified." },
        { id: "8.2", label: "Client advised of final financial position", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "8.3", label: "Key arrangements confirmed for completion day", type: "checkbox", applies: ["sale", "purchase", "linked"] },
        { id: "8.4", label: "Client advised on buildings insurance (from exchange)", type: "checkbox", applies: ["purchase", "linked"] },
      ],
    },
    {
      id: "risk_aml_cqs",
      title: "Risk, AML & CQS / Protocol Checks",
      items: [
        // CQS Enhancement fields for Pre-Completion
        { id: "cqs_file_note_updated_precompletion", label: "Have you updated the file note to record today's completion planning and risk checks?", type: "radio", applies: ["sale", "purchase", "linked"], required: true },
        { id: "cqs_client_timing_explained", label: "Have you explained expected completion timing, chain dependencies and any timing risks to the client?", type: "radio", applies: ["sale", "purchase", "linked"], required: true },
        { id: "cqs_risk_issue_flagged_precompletion", label: "Any outstanding risk issues at this stage that should be flagged for CQS file review or PI risk log?", type: "radio", applies: ["sale", "purchase", "linked"], required: true },
        { id: "cqs_risk_issue_details_precompletion", label: "If Yes, summarise risk and mitigation", type: "multiline", applies: ["sale", "purchase", "linked"], conditionalOn: "cqs_risk_issue_flagged_precompletion", conditionalValue: "yes" },
        { id: "cqs_risk_followup_date_precompletion", label: "Follow-up/review date (e.g. post-completion check-in)", type: "date", applies: ["sale", "purchase", "linked"], conditionalOn: "cqs_risk_issue_flagged_precompletion", conditionalValue: "yes" },
        { id: "cqs_learning_point", label: "Any learning points from this matter for team training / CQS file review meeting?", type: "multiline", applies: ["sale", "purchase", "linked"], required: false },
      ],
    },
    {
      id: "fee_earner_signoff",
      title: "Fee Earner Sign-off",
      items: [
        { id: "9.1", label: "Fee earner confirms all items complete and ready to complete", type: "signature", applies: ["sale", "purchase", "linked"] },
      ],
    },
    {
      id: "post_completion_reference",
      title: "Post-Completion Tasks (for reference)",
      description: "These auto-generate reminders after completion",
      items: [
        { id: "10.1", label: "SDLT/LTT submission within 14 days", type: "checkbox", applies: ["purchase", "linked"], hint: "Auto-reminder" },
        { id: "10.2", label: "Registration at Land Registry within priority period", type: "checkbox", applies: ["purchase", "linked"], hint: "Auto-reminder" },
        { id: "10.3", label: "Serve notice on landlord", type: "checkbox", applies: ["purchase", "linked"], condition: "is_leasehold" },
        { id: "10.4", label: "Send certified copy of transfer to management company", type: "checkbox", applies: ["purchase", "linked"], condition: "is_leasehold" },
        { id: "10.5", label: "Final bill and residual balance to client", type: "checkbox", applies: ["sale", "purchase", "linked"] },
      ],
    },
  ],
};

export default PRE_COMPLETION_CONFIG;
